/**
 * OTWhatsAppChat.jsx · v101.254.0 · Chat de WhatsApp de la OT en la pestaña General (columna izquierda).
 * Muestra la conversación con el cliente (Fibi, agentes y cliente) como chat, se actualiza sola cada 20 s y el botón
 * «Abrir en WhatsApp» lleva a la pantalla principal de Conversaciones con ese chat abierto.
 * Usa GET /v1/fibi/ot/{id} (mismo dato que la evidencia de la OT). Si el usuario no tiene permiso, no se muestra.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Bot, ExternalLink, Loader2, MessageCircle, RefreshCw } from "lucide-react";

const ROLES_AGENTE = ["super_admin", "admin", "admin_zone", "supervisor", "agendador", "scheduler", "atc"];
const ESTADO = { en_espera: "En espera de agente", humano: "Con agente", bot: "Con Fibi", cerrada: "Cerrada" };
const EST_COLOR = { en_espera: "#B45309", humano: "#0F7FA8", bot: "#16A34A", cerrada: "#64748B" };
const hora = (v) => (v ? new Date(v).toLocaleString("es-EC", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");
const autor = (m) => (m.autor === "cliente" ? "Cliente" : m.autor === "fibi" ? "Fibi" : m.autor === "agente" ? (m.autor_nombre || "Agente") : "Sistema");

export default function OTWhatsAppChat({ odooTaskId, alto = 420 }) {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [items, setItems] = useState(undefined);   // undefined = cargando · null = sin permiso
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const fin = useRef(null);
  const agente = ROLES_AGENTE.includes(String(user?.role || "").toLowerCase());

  const load = useCallback(() => {
    if (!odooTaskId) return;
    api.get(`/v1/fibi/ot/${odooTaskId}`)
      .then(({ data }) => {
        const its = (data.items || []).slice().sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
        setItems(its);
        setSel((s) => (s && its.some((c) => c.id === s) ? s : its[0]?.id ?? null));
      })
      .catch((e) => setItems(e?.response?.status === 403 ? null : []));
  }, [odooTaskId]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const conv = (items || []).find((c) => c.id === sel) || null;
  const msgs = (conv?.mensajes || []).filter((m) => (m.texto || "").trim());
  useEffect(() => { fin.current?.scrollIntoView({ block: "end" }); }, [sel, msgs.length]);

  const contactar = async () => {
    setBusy(true);
    try {
      await api.post("/v1/fibi/iniciar", { odoo_task_id: odooTaskId }, { noProcess: true });
      toast.success("Fibi saludó al cliente por WhatsApp");
      load();
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "No se pudo contactar");
    } finally { setBusy(false); }
  };
  const abrir = () => navigate(conv ? `/whatsapp/conversaciones?c=${conv.id}` : "/whatsapp/conversaciones");

  if (items === null) return null;
  return (
    <div data-testid="ot-whatsapp-chat"
      style={{ borderRadius: 20, background: "#ECF0F5", padding: 14,
               boxShadow: "-6px -6px 14px rgba(255,255,255,.95), 6px 6px 14px rgba(163,177,198,.55)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, color: "#0A3747", fontSize: 14 }}>
          <span style={{ width: 28, height: 28, borderRadius: 9, background: "#DCFCE7", color: "#16A34A",
                         display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <MessageCircle size={15} />
          </span>
          WhatsApp del cliente
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={load} title="Actualizar" style={{ border: 0, borderRadius: 9, padding: 6, background: "#E4EAF0", cursor: "pointer" }}>
            <RefreshCw size={13} />
          </button>
          {agente && (
            <button onClick={abrir} data-testid="ot-whatsapp-abrir"
              style={{ border: 0, borderRadius: 10, padding: "6px 10px", background: "#16A34A", color: "#FFF", cursor: "pointer",
                       fontSize: 12, fontWeight: 700, display: "inline-flex", gap: 5, alignItems: "center" }}>
              <ExternalLink size={13} /> Abrir en WhatsApp
            </button>
          )}
        </div>
      </div>

      {items === undefined && <div style={{ fontSize: 12, color: "#64748B", padding: 12 }}>Cargando conversación…</div>}

      {items && items.length > 1 && (
        <select value={sel ?? ""} onChange={(e) => setSel(Number(e.target.value))}
          style={{ width: "100%", fontSize: 12, borderRadius: 8, border: "1px solid #CBD5E1", padding: "4px 6px", marginBottom: 8, background: "#FFF" }}>
          {items.map((c) => (
            <option key={c.id} value={c.id}>#{c.id} · {hora(c.created_at)} · {ESTADO[c.estado] || c.estado}</option>
          ))}
        </select>
      )}

      {conv && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, marginBottom: 6, color: "#475569", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, color: EST_COLOR[conv.estado] || "#475569" }}>● {ESTADO[conv.estado] || conv.estado}</span>
          {conv.asignado_nombre && <span>· {conv.asignado_nombre}</span>}
          <span>· {msgs.filter((m) => m.direccion !== "nota").length} mensajes</span>
        </div>
      )}

      {items && items.length === 0 && (
        <div style={{ fontSize: 12, color: "#64748B", padding: "10px 4px" }}>
          Aún no hay conversación de WhatsApp con el cliente de esta OT.
          {agente && (
            <div style={{ marginTop: 8 }}>
              <button onClick={contactar} disabled={busy}
                style={{ border: 0, borderRadius: 10, padding: "6px 10px", background: "#0A3747", color: "#FFF", cursor: "pointer",
                         fontSize: 12, fontWeight: 600, display: "inline-flex", gap: 5, alignItems: "center" }}>
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Bot size={13} />} Contactar con Fibi
              </button>
            </div>
          )}
        </div>
      )}

      {conv && (
        <div style={{ background: "#EFEAE2", borderRadius: 14, padding: 10, maxHeight: alto, minHeight: 160, overflowY: "auto",
                      display: "flex", flexDirection: "column", gap: 6 }}>
          {conv.resumen && (
            <div style={{ alignSelf: "center", fontSize: 11, background: "#FEF3C7", color: "#78350F", borderRadius: 8, padding: "4px 8px", maxWidth: "95%" }}>
              Resumen: {conv.resumen}
            </div>
          )}
          {msgs.length === 0 && <div style={{ fontSize: 12, color: "#64748B", textAlign: "center" }}>Sin mensajes.</div>}
          {msgs.map((m) => {
            if (m.direccion === "nota") {
              return (
                <div key={m.id} style={{ alignSelf: "center", fontSize: 11, color: "#64748B", fontStyle: "italic", textAlign: "center", maxWidth: "95%" }}>
                  {m.texto}
                </div>
              );
            }
            const cli = m.autor === "cliente";
            return (
              <div key={m.id} style={{ alignSelf: cli ? "flex-start" : "flex-end", maxWidth: "85%" }}>
                <div style={{ background: cli ? "#FFFFFF" : "#D9FDD3", borderRadius: cli ? "2px 12px 12px 12px" : "12px 2px 12px 12px",
                              padding: "6px 9px", fontSize: 12.5, color: "#0F172A", boxShadow: "0 1px 1px rgba(0,0,0,.08)" }}>
                  {!cli && <div style={{ fontSize: 10.5, fontWeight: 700, color: m.autor === "fibi" ? "#16A34A" : "#0F7FA8" }}>{autor(m)}</div>}
                  <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {/^\[sticker:/.test(m.texto || "") ? "🖼️ sticker" : m.texto}
                  </div>
                  <div style={{ fontSize: 10, color: "#64748B", textAlign: "right", marginTop: 2 }}>{hora(m.created_at)}</div>
                </div>
              </div>
            );
          })}
          <div ref={fin} />
        </div>
      )}
      {conv && agente && (
        <div style={{ fontSize: 11, color: "#64748B", marginTop: 6 }}>
          Para responder al cliente pulse «Abrir en WhatsApp».
        </div>
      )}
    </div>
  );
}
